/**
 * Guided cooking.
 *
 * Three things this screen must never do:
 *
 *  1. Invent a number. An oven step whose post gave no temperature ASKS for one;
 *     it does not helpfully suggest 350.
 *  2. Start a timer because a step was reached. The food goes in when she says
 *     it goes in, so an oven step is gated behind "It's in the oven".
 *  3. Promise an alarm it cannot deliver. A browser tab that is closed does not
 *     ring, and this screen says so in plain words rather than implying a
 *     background alarm that will not arrive.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from '../Icon'
import { sound } from '../../lib/fun'
import { Attention, IngredientList, SourceLink } from './Bits'
import {
  abandonSession, adjustTimer, beginCooking, clockFor, completeSession, dropTimer,
  formatDuration, formatQuantity, getSession, readiness, saveProgress, startTimer,
  type Readiness, type Recipe, type Session, type Step,
} from '../../lib/savenmake'

type Phase = 'prep' | 'cooking' | 'review'

/** Kitchen shorthand. "6 tablespoon butter" is not how anybody reads a recipe. */
const SHORT: Record<string, string> = {
  tablespoon: 'tbsp', teaspoon: 'tsp', gram: 'g', kilogram: 'kg', ounce: 'oz',
  pound: 'lb', milliliter: 'ml', liter: 'L', cup: 'cup', clove: 'clove',
  pinch: 'pinch', slice: 'slice', can: 'can', stick: 'stick', sprig: 'sprig',
}
const shortUnit = (u: string) => SHORT[u] ?? u

export default function Cook({ recipeId, sessionId, onDone, onOpenRecipe }: {
  recipeId: string
  sessionId?: string
  onDone: () => void
  onOpenRecipe: (id: string) => void
}) {
  const [phase, setPhase] = useState<Phase>(sessionId ? 'cooking' : 'prep')
  const [ready, setReady] = useState<{ readiness: Readiness; recipe: Recipe } | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        if (sessionId) setSession(await getSession(sessionId))
        else setReady(await readiness(recipeId))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not open this recipe.')
      }
    })()
  }, [recipeId, sessionId])

  const begin = async () => {
    setBusy(true)
    try {
      const { session: s } = await beginCooking(recipeId)
      setSession(s)
      setPhase('cooking')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start.')
    } finally { setBusy(false) }
  }

  if (error) return <div className="card"><p className="error">{error}</p><button onClick={onDone}>Back</button></div>
  if (phase === 'prep' && ready) return <Prep data={ready} busy={busy} onBegin={begin} onCancel={onDone} />
  if (session && phase === 'cooking') {
    return (
      <Cooking
        session={session} setSession={setSession}
        onFinish={() => setPhase('review')}
        onAbandon={async () => { await abandonSession(session.id); onDone() }}
      />
    )
  }
  if (session && phase === 'review') {
    return <Review session={session} onSaved={onDone} onOpenRecipe={() => onOpenRecipe(session.recipeId)} />
  }
  return <p className="muted">Loading…</p>
}

/* ── before the first step ──────────────────────────────────────────────── */

function Prep({ data, busy, onBegin, onCancel }: {
  data: { readiness: Readiness; recipe: Recipe }
  busy: boolean
  onBegin: () => void
  onCancel: () => void
}) {
  const { readiness: r, recipe } = data
  return (
    <div className="cook">
      <button type="button" className="ghost" onClick={onCancel}><Icon name="back" size={16} /> Back</button>
      <h1>{recipe.title}</h1>
      <SourceLink url={recipe.sourceUrl} platform={recipe.sourcePlatform} creator={recipe.sourceCreator} />

      <Attention flags={r.attention} />

      {!!r.blocking.length && (
        <div className="attention">
          <h3><Icon name="alert" size={16} /> The post never gave these</h3>
          <ul>
            {r.blocking.map((b, i) => (
              <li key={i}>
                <b>Step {b.stepPosition + 1}</b> — no {b.missing}. &ldquo;{b.text.slice(0, 70)}
                {b.text.length > 70 ? '…' : ''}&rdquo;
              </li>
            ))}
          </ul>
          {/* Said out loud, because the alternative is an app that quietly
              picks a number and lets her find out at the oven. */}
          <p className="small" style={{ marginBottom: 0 }}>
            You can start anyway — it will ask when you reach the step rather than guess.
          </p>
        </div>
      )}

      <div className="card">
        <IngredientList ingredients={recipe.ingredients} />
        {!!recipe.equipment.length && (
          <div className="cook-ingredients" style={{ marginTop: 14 }}>
            <h4>You&rsquo;ll want</h4>
            <ul>{recipe.equipment.map((e) => <li key={e.id}>{e.name}</li>)}</ul>
          </div>
        )}
      </div>

      {!r.ready && <p className="warn">This recipe has no steps yet. Add the method before cooking from it.</p>}

      <div className="cook-nav">
        <button type="button" onClick={onCancel}>Not now</button>
        <button type="button" className="primary" onClick={onBegin} disabled={busy || !r.ready}>
          <Icon name="flame" size={18} /> {busy ? 'Starting…' : 'Begin recipe'}
        </button>
      </div>
    </div>
  )
}

/* ── the step-by-step ───────────────────────────────────────────────────── */

function Cooking({ session, setSession, onFinish, onAbandon }: {
  session: Session
  setSession: (s: Session) => void
  onFinish: () => void
  onAbandon: () => void
}) {
  const recipe = session.snapshot
  const steps = recipe.steps
  const [at, setAt] = useState(Math.min(session.currentStep, Math.max(0, steps.length - 1)))
  const step = steps[at]

  /* A one-second heartbeat purely to re-render the clocks. The deadlines are
     absolute and stored, so this ticking is cosmetic — if it stops, the timers
     are still correct the moment anything reads them again. */
  const [, setTick] = useState(0)
  useEffect(() => {
    const h = setInterval(() => setTick((n) => n + 1), 1000)
    return () => clearInterval(h)
  }, [])

  const confirmations = session.confirmations
  const key = String(at)
  const given = confirmations[key] ?? []

  const move = useCallback(async (to: number) => {
    const next = Math.max(0, Math.min(steps.length - 1, to))
    setAt(next)
    setSession(await saveProgress(session.id, { currentStep: next }))
  }, [session.id, steps.length, setSession])

  const confirm = async (token: string) => {
    const merged = { ...confirmations, [key]: [...new Set([...given, token])] }
    setSession(await saveProgress(session.id, { confirmations: merged }))
  }

  if (!step) return <p className="muted">This recipe has no steps.</p>

  const usedIngredients = recipe.ingredients.filter((i) => step.ingredientRefs.includes(i.id))
  const gate = gateFor(step, given)

  return (
    <div className="cook">
      <div className="filter-row" style={{ marginBottom: 12 }}>
        <button type="button" className="ghost" onClick={onAbandon}>
          <Icon name="close" size={16} /> Stop cooking
        </button>
        <span className="muted small" style={{ marginLeft: 'auto' }}>
          {/* Abandoning is not cooking. Said here so the rule is visible where
              it applies, not only in the history that would have been wrong. */}
          Stopping does not record this as cooked.
        </span>
      </div>

      {session.stale && (
        <p className="warn">
          This recipe was edited after you started. You are cooking from the version you began with.
        </p>
      )}

      <TimerRail session={session} setSession={setSession} />

      <div className="filter-row" style={{ marginBottom: 12 }}>
        <SourceLink url={recipe.sourceUrl} platform={recipe.sourcePlatform} subtle />
      </div>

      {gate && <Gate step={step} gate={gate} onConfirm={confirm} session={session} setSession={setSession} at={at} />}

      <div className="cook-step">
        <span className="cook-step-n">Step {at + 1} of {steps.length}</span>
        <p className="cook-step-text">{step.text}</p>

        {step.cue && (
          <p className="cook-cue">
            <Icon name="info" size={15} /> <b>Look for:</b> {step.cue}
          </p>
        )}

        {/*
          * How much of what, right under the instruction.
          *
          * The step says "melt the butter"; this says "6 tbsp". Set smaller than
          * the instruction and in the brand green so it reads as supporting
          * detail rather than competing with the thing she is doing — she is
          * looking at this from arm's length with her hands full.
          */}
        {!!usedIngredients.length && (
          <ul className="step-amounts">
            {usedIngredients.map((i) => (
              <li key={i.id}>
                <b>{[formatQuantity(i.quantity), i.unit ? shortUnit(i.unit) : ''].filter(Boolean).join(' ') || 'some'}</b>
                <span>{i.item}</span>
                {i.note && <em>{i.note}</em>}
              </li>
            ))}
          </ul>
        )}

        {step.temperature !== null && (
          <p className="cook-cue"><b>{step.temperature}°{step.temperatureUnit}</b></p>
        )}
        {step.durationSeconds !== null && !gate && (
          <button
            type="button" style={{ marginTop: 14 }}
            onClick={async () => setSession(await startTimer(session.id, step.text.slice(0, 40), step.durationSeconds!, at))}
          >
            <Icon name="timer" size={16} /> Start a {formatDuration(step.durationSeconds)} timer
          </button>
        )}
      </div>

      {/* Always reachable, never in the way of the step. */}
      <details style={{ marginBottom: 16 }}>
        <summary style={{ cursor: 'pointer', padding: '10px 0', fontWeight: 600 }}>All ingredients</summary>
        <IngredientList ingredients={recipe.ingredients} />
      </details>

      <div className="cook-nav">
        <button type="button" onClick={() => void move(at - 1)} disabled={at === 0}>
          <Icon name="back" size={18} /> Back
        </button>
        {at < steps.length - 1 ? (
          <button type="button" className="primary" onClick={() => void move(at + 1)}>
            Next <Icon name="chevron" size={18} />
          </button>
        ) : (
          <button type="button" className="primary" onClick={onFinish}>
            <Icon name="check" size={18} /> Finish
          </button>
        )}
      </div>
    </div>
  )
}

/* ── the confirmation gate ──────────────────────────────────────────────── */

interface GateSpec { kind: 'preheat' | 'in' ; question: string }

/**
 * What this step needs confirmed before it can proceed.
 *
 * An oven step asks twice: that the oven is at temperature, then that the food
 * is actually IN it — and only the second starts the timer. The gap between
 * those two is where every over-baked tray comes from, and an app that starts
 * counting when the step appears is timing the wrong thing.
 */
function gateFor(step: Step, given: string[]): GateSpec | null {
  if (step.actionKind !== 'oven') return null
  if (!/\b(bake|roast|broil|oven)\b/i.test(step.text)) return null
  if (!given.includes('preheated')) return { kind: 'preheat', question: 'Is the oven up to temperature?' }
  if (step.durationSeconds !== null && !given.includes('in')) return { kind: 'in', question: 'In it goes.' }
  return null
}

function Gate({ step, gate, onConfirm, session, setSession, at }: {
  step: Step
  gate: GateSpec
  onConfirm: (token: string) => void | Promise<void>
  session: Session
  setSession: (s: Session) => void
  at: number
}) {
  const [temp, setTemp] = useState(step.temperature === null ? '' : String(step.temperature))
  const [unit, setUnit] = useState<'F' | 'C'>(step.temperatureUnit ?? 'F')

  if (gate.kind === 'preheat') {
    return (
      <div className="confirm-gate">
        <h3>{gate.question}</h3>
        {step.temperature !== null ? (
          <p className="big">{step.temperature}°{step.temperatureUnit}</p>
        ) : (
          /* The post never said. So it is asked, plainly, and nothing is
             pre-filled — a default here would be a guess wearing her handwriting. */
          <>
            <p style={{ margin: 0 }}>
              <b>The post never gave a temperature.</b> What are you setting it to?
            </p>
            <div className="filter-row">
              <input
                type="number" inputMode="numeric" value={temp} onChange={(e) => setTemp(e.target.value)}
                placeholder="e.g. 375" style={{ maxWidth: 140 }} aria-label="Oven temperature"
              />
              <select value={unit} onChange={(e) => setUnit(e.target.value as 'F' | 'C')} style={{ maxWidth: 90 }} aria-label="Unit">
                <option value="F">°F</option>
                <option value="C">°C</option>
              </select>
            </div>
          </>
        )}
        <button
          type="button" className="primary"
          disabled={step.temperature === null && !temp}
          onClick={() => void onConfirm('preheated')}
        >
          <Icon name="check" size={18} /> Yes, it&rsquo;s preheated
        </button>
      </div>
    )
  }

  return (
    <div className="confirm-gate">
      <h3>{gate.question}</h3>
      <p style={{ margin: 0 }}>
        The timer starts when you say the food is in — not before.
        {step.durationSeconds !== null && <> It will run for <b>{formatDuration(step.durationSeconds)}</b>.</>}
      </p>
      <button
        type="button" className="primary"
        onClick={async () => {
          if (step.durationSeconds !== null) {
            setSession(await startTimer(session.id, step.text.slice(0, 40), step.durationSeconds, at))
          }
          await onConfirm('in')
        }}
      >
        <Icon name="timer" size={18} /> It&rsquo;s in the oven
      </button>
    </div>
  )
}

/* ── timers ─────────────────────────────────────────────────────────────── */

function TimerRail({ session, setSession }: { session: Session; setSession: (s: Session) => void }) {
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [minutes, setMinutes] = useState('10')
  const running = session.timers.filter((t) => t.status !== 'stopped')

  /*
   * Honesty about what an alert can do.
   *
   * A page that is closed cannot ring, and a tab in the background may be
   * throttled. Notification permission helps and is not a guarantee, so the
   * copy says "while this stays open" rather than implying a background alarm.
   */
  const notify = useRef(false)
  useEffect(() => {
    if (typeof Notification === 'undefined') return
    notify.current = Notification.permission === 'granted'
  }, [])

  const rung = useRef(new Set<string>())
  useEffect(() => {
    for (const t of running) {
      if (t.deadlineAt <= Date.now() && !rung.current.has(t.id)) {
        rung.current.add(t.id)
        if (notify.current) {
          try { new Notification(`${t.name} — time to check`, { body: 'Have a look before calling it done.', tag: t.id }) } catch { /* denied mid-session */ }
        }
      }
    }
  })

  return (
    <div className="timer-rail">
      {running.map((t) => {
        const remaining = t.deadlineAt - Date.now()
        const rang = remaining <= 0
        const step = t.stepPosition !== null ? session.snapshot.steps[t.stepPosition] : undefined
        return (
          <div key={t.id} className={`timer ${rang ? 'is-rang' : ''}`}>
            <span className="timer-name">{t.name}</span>
            <span className="timer-clock">{clockFor(remaining)}</span>
            {rang ? (
              <>
                {/* A timer ending is a prompt to LOOK, not a verdict that the
                    food is done. The recipe's own cue is what it says to check. */}
                <span className="small" style={{ flexBasis: '100%' }}>
                  {step?.cue ? <>Check: <b>{step.cue}</b>. Not done? Give it longer.</> : <>Time to check. Not done? Give it longer.</>}
                </span>
                <button type="button" onClick={async () => setSession(await adjustTimer(t.id, { addSeconds: 120 }))}>+2 min</button>
                <button type="button" onClick={async () => setSession(await adjustTimer(t.id, { addSeconds: 300 }))}>+5 min</button>
                <button type="button" className="accent" onClick={async () => setSession(await dropTimer(t.id))}>
                  <Icon name="check" size={15} /> Done
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={async () => setSession(await adjustTimer(t.id, { addSeconds: 60 }))}>+1 min</button>
                <button type="button" className="ghost" onClick={async () => setSession(await dropTimer(t.id))} aria-label={`Cancel ${t.name}`}>
                  <Icon name="close" size={15} />
                </button>
              </>
            )}
          </div>
        )
      })}

      {adding ? (
        <form
          className="timer"
          onSubmit={async (e) => {
            e.preventDefault()
            const secs = Math.round(Number(minutes) * 60)
            if (!secs) return
            setSession(await startTimer(session.id, name.trim() || 'Timer', secs))
            setName(''); setMinutes('10'); setAdding(false)
          }}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="What for? e.g. Dough rising" aria-label="Timer name" style={{ flex: '1 1 140px', marginTop: 0 }} />
          <input type="number" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} aria-label="Minutes" style={{ flex: '0 0 80px', marginTop: 0 }} />
          <button type="submit" className="primary" style={{ width: 'auto' }}>Start</button>
          <button type="button" className="ghost" onClick={() => setAdding(false)}>Cancel</button>
        </form>
      ) : (
        <button type="button" onClick={() => setAdding(true)}>
          <Icon name="timer" size={16} /> Another timer
        </button>
      )}

      {!!running.length && (
        <p className="muted small" style={{ margin: 0 }}>
          Timers keep counting if you close the app — the time is stored, not the countdown.
          {typeof Notification !== 'undefined' && Notification.permission !== 'granted'
            ? ' Alerts only show while this page is open.'
            : ' Alerts arrive while this page is open.'}
        </p>
      )}
    </div>
  )
}

/* ── the post-cooking review ────────────────────────────────────────────── */

function Review({ session, onSaved, onOpenRecipe }: {
  session: Session
  onSaved: () => void
  onOpenRecipe: () => void
}) {
  const [servings, setServings] = useState(session.snapshot.servings ? String(session.snapshot.servings) : '')
  const [notes, setNotes] = useState('')
  const [adjustments, setAdjustments] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [fresh, setFresh] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const SUGGESTED = ['Great for a crowd', 'Easy cleanup', 'Cafe hit', 'Freezes well', 'Would halve it', 'Took longer than it said']

  const save = async () => {
    if (busy || done) return
    // In the tap, before the await: iOS only lets audio start there.
    sound('cooked')
    setBusy(true)
    try {
      await completeSession(session.id, {
        servingsActual: servings ? Number(servings) : null,
        notes: notes.trim() || null,
        adjustments: adjustments.trim() || null,
        tags,
      })
      setDone(true)
      onSaved()
    } finally { setBusy(false) }
  }

  return (
    <div className="cook">
      <h1>Nicely done.</h1>
      <p className="muted">
        All optional — skip any of it. This gets saved against <b>{session.snapshot.title}</b> and the recipe
        moves to <b>Tested</b>.
      </p>

      <div className="card">
        <label>
          How many did it actually make?
          <input type="number" inputMode="numeric" value={servings} onChange={(e) => setServings(e.target.value)} placeholder={session.snapshot.servings ? `The recipe said ${session.snapshot.servings}` : 'e.g. 12'} />
        </label>
        <label>
          Anything you changed?
          <textarea value={adjustments} onChange={(e) => setAdjustments(e.target.value)} rows={3} placeholder="Half the sugar, added a bay leaf, 10 minutes longer…" />
        </label>
        <label>
          Notes for next time
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Crust was perfect. Start the dough the night before." />
        </label>

        <label>How did it go?</label>
        <div className="chips" style={{ marginBottom: 16 }}>
          {[...new Set([...SUGGESTED, ...tags])].map((tag) => (
            <button
              key={tag} type="button"
              className="chip" aria-pressed={tags.includes(tag)}
              style={tags.includes(tag) ? { borderColor: 'var(--brand)', borderWidth: 2, color: 'var(--brand)' } : undefined}
              onClick={() => setTags((t) => (t.includes(tag) ? t.filter((x) => x !== tag) : [...t, tag]))}
            >
              {tag}
            </button>
          ))}
        </div>
        <div className="filter-row">
          <input
            className="grow" value={fresh} onChange={(e) => setFresh(e.target.value)}
            placeholder="Or a new one of your own" aria-label="New tag"
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || !fresh.trim()) return
              e.preventDefault()
              setTags((t) => [...new Set([...t, fresh.trim()])])
              setFresh('')
            }}
          />
          <button type="button" onClick={() => { if (fresh.trim()) { setTags((t) => [...new Set([...t, fresh.trim()])]); setFresh('') } }}>
            <Icon name="plus" size={16} /> Add
          </button>
        </div>
      </div>

      <p className="muted small">
        Marking it <b>Cafe approved</b> is yours to decide — do that from the recipe when you are sure.
        {' '}<button type="button" className="link" onClick={onOpenRecipe}>Open the recipe</button>
      </p>

      <div className="cook-nav">
        {/* Disabled the moment it is pressed. The server refuses a second run
            anyway — this only saves her the confusing double flash. */}
        <button type="button" className="primary" onClick={save} disabled={busy || done}>
          <Icon name="check" size={18} /> {busy ? 'Saving…' : 'Save and finish'}
        </button>
      </div>
    </div>
  )
}
